import { apiClient } from './api-client';

// ============================================================================
// INTERFACES
// ============================================================================

/**
 * Current withdrawal request status
 */
export interface CurrentWithdrawalRequestStatus {
    requestId: number;
    status: 'PENDING' | 'IN_PROGRESS';
    submittedDate: string; // ISO date string
    estimatedCompletionDate: string; // ISO date string
    slaDays: number;
    reason?: string;
}

/**
 * Individual purpose object from the API
 */
export interface Purpose {
    purposeId: number;
    purposeKey: string;
    purposeName: string;
    purposeDescription: string;
    lawfulGround: string;
    lawfulGroundLabel: string;
    currentConsent: boolean;
    lastConsentDate: string; // ISO date string
    retentionExpirationDate?: string; // ISO date string
    legalRetentionRequired: boolean;
    legalRetentionPeriod?: number;
    endOnConsentWithdrawn: boolean;
    endOnRetentionExpired: boolean;
    endOnPurposeFulfilled: boolean;
    endOnErasureRequested: boolean;
    endOnAccountClosure: boolean;
    status: 'ACTIVE' | 'WITHDRAWN' | 'EXPIRED' | 'LEGAL_RETENTION_ACTIVE' | 'LEGAL_RETENTION_EXPIRED';
    canModify: boolean;
    withdrawalRequest?: CurrentWithdrawalRequestStatus;
}

/**
 * API response for getting purposes
 */
export interface PurposesResponse {
    success: boolean;
    purposes: Purpose[];
}

/**
 * Collection point information in history
 */
export interface CollectionPointInfo {
    id: number;
    name: string;
    description: string;
    identifier: string;
    type: string;
}

/**
 * Location information in history
 */
export interface LocationInfo {
    country: string;
    region: string;
    city: string;
    latitude: number;
    longitude: number;
}

/**
 * Purpose history item
 */
export interface PurposeHistoryItem {
    consentDetailId: number;
    consentHeaderId: number;
    consentValue: boolean;
    consentDate: string; // ISO date string
    retentionExpirationDate?: string; // ISO date string
    isTest: boolean;
    collectionPoint: CollectionPointInfo;
    consentSource: string;
    isMinor?: boolean;
    ipAddress: string;
    location?: LocationInfo; // Optional - may not be available for all consent records
    languageCode: string;
    userAgent: string;
}

/**
 * API response for purpose history
 */
export interface PurposeHistoryResponse {
    success: boolean;
    history: PurposeHistoryItem[];
}

/**
 * Withdrawal request history item
 */
export interface WithdrawalHistoryItem {
    requestId: number;
    requestStatus: string;
    createdDate: string; // ISO date string
    estimatedCompletionDate: string | null; // ISO date string
    actualCompletionDate: string | null; // ISO date string
    reason: string | null;
    purposeKey: string | null;
    modifiedDate: string | null; // ISO date string
}

/**
 * API response for withdrawal history
 */
export interface WithdrawalHistoryResponse {
    success: boolean;
    data: WithdrawalHistoryItem[];
}

/**
 * Collection point information in consent record
 */
export interface ConsentCollectionPoint {
    id: number;
    name: string;
    description: string;
    identifier: string;
    type: string;
    snapshot: any; // Complex snapshot object with purposes, data elements, etc.
}

/**
 * Data element in consent record
 */
export interface ConsentDataElement {
    dataElementId: number;
    dataElementKey: string;
    elementName: string;
    dataElementValue: string;
    dataElementType: string;
    isPersonalData: boolean;
    vaultId?: number; // Vault ID for personal data elements
    translations?: Array<{
        languageCode: string;
        translatedName: string;
        translatedDescription: string;
    }>;
}

export interface CollectionPointDataElement {
    id: number;
    key: string;
    name: string;
    description: string;
    code: string;
    dataType: string;
    dataTypeId: number;
    dataTypeExplanation: string;
    isRequired: boolean;
    isPersonalData: boolean;
    isIdentifier: boolean;
    typeId: number;
    validationRules: any;
    isMultiSelect: boolean;
    translations: Array<{
        languageCode: string;
        translatedName: string;
        translatedDescription: string;
    }>;
}

/**
 * Purpose in consent record
 */
export interface ConsentPurpose {
    purposeId: number;
    purposeKey: string;
    purposeName: string;
    consentValue: boolean;
    retentionExpirationDate?: string;
}

/**
 * Full consent record
 */
export interface ConsentRecord {
    consentHeaderId: number;
    consentDate: string; // ISO date string
    collectionPoint: ConsentCollectionPoint;
    consentSource: string;
    ipAddress: string;
    location: LocationInfo;
    languageCode: string;
    userAgent: string;
    purposes: ConsentPurpose[];
    dataElements: ConsentDataElement[];
    privacyPolicy?: any;
    yourRights?: any;
    contactDpo?: any;
}

/**
 * DPO contact information interface
 */
export interface DPOContactInfo {
    title: string;
    effectiveDate: string;
    lastUpdated: string;
    version: string;
    complianceText: string;
    documentInfo: string;
    content: string; // HTML content
}

/**
 * API response for consent record
 */
export interface ConsentRecordResponse {
    success: boolean;
    consentRecord: ConsentRecord;
}

/**
 * API response for withdraw/grant actions
 */
export interface PurposeActionResponse {
    success: boolean;
    message: string;
    newConsentHeaderId?: number;
}

/**
 * Withdrawal request payload
 */
export interface WithdrawalRequestPayload {
    purposeId: number;
    reason?: string;
}

/**
 * Withdrawal request response
 */
export interface WithdrawalRequestResponse {
    requestId: number;
    status: string;
    purposeId: number;
    purposeName: string;
    purposeKey: string;
    submittedDate: Date;
    estimatedCompletionDate: Date;
    message: string;
}

// ============================================================================
// PURPOSE SERVICE CLASS
// ============================================================================

export class PurposeService {
    private baseUrl: string;

    constructor() {
        this.baseUrl = '/portal';
    }

    /**
     * Get all purposes for the authenticated DP
     */
    async getPurposes(activeOnly: boolean = false): Promise<Purpose[]> {
        try {
            const url = `${this.baseUrl}/purposes?activeOnly=${activeOnly}`;
            const response = await apiClient.get<PurposesResponse>(url);

            if (!response.success) {
                throw new Error('Failed to fetch purposes');
            }

            return response.purposes;
        } catch (error) {
            console.error('Error fetching purposes:', error);
            throw error;
        }
    }

    /**
     * Get consent history for a specific purpose
     */
    async getPurposeHistory(purposeId: number): Promise<PurposeHistoryResponse> {
        try {
            const url = `${this.baseUrl}/purposes/${purposeId}/history`;
            const response = await apiClient.get<PurposeHistoryResponse>(url);

            if (!response.success) {
                throw new Error('Failed to fetch purpose history');
            }

            return response;
        } catch (error) {
            console.error('Error fetching purpose history:', error);
            throw error;
        }
    }

    /**
     * Get withdrawal request history for a specific purpose
     */
    async getPurposeWithdrawalHistory(purposeId: number): Promise<WithdrawalHistoryResponse> {
        try {
            const url = `${this.baseUrl}/purposes/${purposeId}/withdrawal-history`;
            const response = await apiClient.get<WithdrawalHistoryResponse>(url);

            if (!response.success) {
                throw new Error('Failed to fetch withdrawal history');
            }

            return response;
        } catch (error) {
            console.error('Error fetching withdrawal history:', error);
            throw error;
        }
    }

    /**
     * Get full consent record details
     */
    async getConsentRecord(consentHeaderId: number): Promise<ConsentRecord> {
        try {
            const url = `${this.baseUrl}/consent/${consentHeaderId}`;
            const response = await apiClient.get<ConsentRecordResponse>(url);

            if (!response.success) {
                throw new Error('Failed to fetch consent record');
            }

            return response.consentRecord;
        } catch (error) {
            console.error('Error fetching consent record:', error);
            throw error;
        }
    }

    /**
     * Create a withdrawal request for a specific purpose
     */
    async createWithdrawalRequest(purposeId: number, reason?: string): Promise<WithdrawalRequestResponse> {
        try {
            const payload: WithdrawalRequestPayload = {
                purposeId,
                reason
            };

            const response = await apiClient.post<WithdrawalRequestResponse>(`${this.baseUrl}/withdrawal-requests`, payload);

            return response;
        } catch (error) {
            console.error('Error creating withdrawal request:', error);
            throw error;
        }
    }

    /**
     * Withdraw consent for a specific purpose (DEPRECATED - use createWithdrawalRequest)
     * @deprecated Use createWithdrawalRequest instead
     */
    async withdrawPurpose(purposeId: number): Promise<PurposeActionResponse> {
        console.warn('withdrawPurpose is deprecated. Use createWithdrawalRequest instead.');

        try {
            const withdrawalResponse = await this.createWithdrawalRequest(purposeId);

            // Convert new response format to old format for backwards compatibility
            return {
                success: true,
                message: withdrawalResponse.message,
                newConsentHeaderId: undefined // Not available in new system
            };
        } catch (error) {
            console.error('Error withdrawing purpose consent:', error);
            throw error;
        }
    }

    /**
     * Grant consent for a specific purpose
     */
    async grantPurpose(purposeId: number): Promise<PurposeActionResponse> {
        try {
            const url = `${this.baseUrl}/purposes/${purposeId}/grant`;
            const response = await apiClient.post<PurposeActionResponse>(url);

            if (!response.success) {
                throw new Error('Failed to grant consent');
            }

            return response;
        } catch (error) {
            console.error('Error granting consent:', error);
            throw error;
        }
    }

    /**
     * Get purposes with error handling and loading states
     */
    async getPurposesWithState(activeOnly: boolean = false): Promise<{
        data: Purpose[] | null;
        loading: boolean;
        error: string | null;
    }> {
        try {
            const data = await this.getPurposes(activeOnly);
            return {
                data,
                loading: false,
                error: null
            };
        } catch (error: any) {
            return {
                data: null,
                loading: false,
                error: error.message || 'Failed to load purposes'
            };
        }
    }

    /**
     * Get purpose history with error handling and loading states
     */
    async getPurposeHistoryWithState(purposeId: number): Promise<{
        data: PurposeHistoryResponse | null;
        loading: boolean;
        error: string | null;
    }> {
        try {
            const data = await this.getPurposeHistory(purposeId);
            return {
                data,
                loading: false,
                error: null
            };
        } catch (error: any) {
            return {
                data: null,
                loading: false,
                error: error.message || 'Failed to load purpose history'
            };
        }
    }

    /**
     * Get DPO contact information for the authenticated DP's client
     */
    async getDPOContact(): Promise<DPOContactInfo> {
        try {
            const url = '/portal/dpo-contact';
            const response = await apiClient.get<{ success: boolean; data: DPOContactInfo; error?: string }>(url);

            if (!response.success) {
                throw new Error(response.error || 'Failed to fetch DPO contact information');
            }

            return response.data;
        } catch (error) {
            console.error('Error fetching DPO contact information:', error);
            throw error;
        }
    }
}

// Create and export a singleton instance
export const purposeService = new PurposeService();

// Export default
export default purposeService;
